import * as THREE from 'three';
import { BlendPreviewGuideDomRenderer } from './BlendPreviewGuideDomRenderer';
import type {
  BlendPreviewGuideOverlayState,
  BlendPreviewGuideViewportAdapter,
  ProjectedBlendPreviewGuide,
} from './types';

type BlendPreviewGuideOverlayControllerOptions = {
  /** 读取当前视口适配器。 */
  getViewport: () => BlendPreviewGuideViewportAdapter | null;
  /** 自定义帧调度。 */
  requestFrame?: (callback: FrameRequestCallback) => number;
  /** 自定义帧取消。 */
  cancelFrame?: (handle: number) => void;
  /** 控制点点击回调。 */
  onControlPointSelect?: (key: string) => void;
};

/**
 * 构建默认的浏览器帧调度函数。
 *
 * 设计说明：
 * 1. `requestAnimationFrame` / `cancelAnimationFrame` 在浏览器中是依附于全局对象的方法；
 * 2. 直接把方法引用缓存到字段上，后续再以普通函数调用时会丢失调用上下文，触发 `Illegal invocation`；
 * 3. 因此这里统一包装为闭包，确保每次都通过 `globalThis` 发起调用，避免控制器持有未绑定的方法引用。
 */
function createDefaultFrameScheduler(): {
  requestFrame: (callback: FrameRequestCallback) => number;
  cancelFrame: (handle: number) => void;
} {
  return {
    requestFrame: (callback) => globalThis.requestAnimationFrame(callback),
    cancelFrame: (handle) => globalThis.cancelAnimationFrame(handle),
  };
}

/**
 * 圆角/倒角预览引导控制器。
 *
 * 设计说明：
 * 1. 引导需要跟随相机变化持续重投影，因此必须有独立控制器管理逐帧刷新；
 * 2. 控制器只关心“世界点 -> 屏幕点”的投影与刷新节奏，具体 DOM 细节交给渲染器；
 * 3. 这样可以在不依赖 Vue 生命周期细节的前提下，把预览引导作为可复用的核心 UI 模块管理。
 */
export class BlendPreviewGuideOverlayController {
  /** DOM 渲染器。 */
  private readonly renderer: BlendPreviewGuideDomRenderer;
  /** 读取视口适配器。 */
  private readonly getViewport: () => BlendPreviewGuideViewportAdapter | null;
  /** 帧调度函数。 */
  private readonly requestFrame: (callback: FrameRequestCallback) => number;
  /** 帧取消函数。 */
  private readonly cancelFrame: (handle: number) => void;
  /** 最近一次状态。 */
  private latestState: BlendPreviewGuideOverlayState | null = null;
  /** 当前帧句柄。 */
  private frameHandle: number | null = null;
  /** 投影计算复用向量，避免预览引导层逐帧刷新时持续分配 THREE 对象。 */
  private readonly projectedPoint = new THREE.Vector3();
  /** 投影结果复用数组，避免逐帧 map/filter 产生临时数组。 */
  private readonly projectedGuides: ProjectedBlendPreviewGuide[] = [];

  /**
   * @param options 控制器参数。
   */
  public constructor(options: BlendPreviewGuideOverlayControllerOptions) {
    const defaultFrameScheduler = createDefaultFrameScheduler();
    this.renderer = new BlendPreviewGuideDomRenderer({
      onControlPointSelect: options.onControlPointSelect,
    });
    this.getViewport = options.getViewport;
    this.requestFrame = options.requestFrame ?? defaultFrameScheduler.requestFrame;
    this.cancelFrame = options.cancelFrame ?? defaultFrameScheduler.cancelFrame;
  }

  /**
   * 同步最新状态。
   *
   * @param state 预览引导状态。
   */
  public sync(state: BlendPreviewGuideOverlayState): void {
    this.latestState = state;
    const hasProjectedGuides = this.renderCurrentState();
    if (state.active && hasProjectedGuides) {
      this.startFrameLoop();
      return;
    }
    this.stopFrameLoop();
  }

  /**
   * 清空引导。
   */
  public clear(): void {
    this.latestState = null;
    this.stopFrameLoop();
    this.renderer.clear();
  }

  /**
   * 释放控制器。
   */
  public dispose(): void {
    this.clear();
    this.renderer.dispose();
  }

  /**
   * 渲染当前状态。
   *
   * @returns 是否存在可见引导。
   */
  private renderCurrentState(): boolean {
    const viewport = this.getViewport();
    const container = viewport?.getContainer() ?? null;
    const camera = viewport?.getCamera() ?? null;
    if (!viewport || !container || !camera || !this.latestState?.active) {
      this.renderer.clear();
      return false;
    }

    const containerRect = container.getBoundingClientRect();
    this.projectedGuides.length = 0;
    for (const guide of this.latestState.guides) {
      const projectedGuide = this.projectGuide(guide, viewport, containerRect, camera);
      if (projectedGuide) {
        this.projectedGuides.push(projectedGuide);
      }
    }
    this.renderer.render(this.projectedGuides, container);
    return this.projectedGuides.length > 0;
  }

  /**
   * 启动逐帧刷新。
   */
  private startFrameLoop(): void {
    if (this.frameHandle !== null) {
      return;
    }

    const tick = (): void => {
      if (!this.renderCurrentState()) {
        this.frameHandle = null;
        return;
      }
      this.frameHandle = this.requestFrame(tick);
    };

    this.frameHandle = this.requestFrame(tick);
  }

  /**
   * 停止逐帧刷新。
   */
  private stopFrameLoop(): void {
    if (this.frameHandle === null) {
      return;
    }
    this.cancelFrame(this.frameHandle);
    this.frameHandle = null;
  }

  /**
   * 投影单条引导。
   *
   * @param guide 原始引导。
   * @param viewport 视口适配器。
   * @param container 容器。
   * @param camera 相机。
   * @returns 屏幕投影结果。
   */
  private projectGuide(
    guide: BlendPreviewGuideOverlayState['guides'][number],
    viewport: BlendPreviewGuideViewportAdapter,
    containerRect: DOMRect,
    camera: THREE.Camera,
  ): ProjectedBlendPreviewGuide | null {
    const worldPoint = this.resolveGuideWorldPoint(guide, viewport);
    if (!worldPoint) {
      return null;
    }

    const screenAnchor = this.projectWorldPoint(worldPoint, containerRect, camera);
    if (!screenAnchor) {
      return null;
    }

    const label = guide.kind === 'balloon'
      ? this.resolveBalloonLabelPoint(guide, viewport, containerRect, camera, screenAnchor)
      : screenAnchor;

    return {
      kind: guide.kind,
      id: guide.id,
      anchor: screenAnchor,
      leaderAnchors: guide.kind === 'balloon'
        ? this.projectBalloonLeaderAnchors(guide, viewport, containerRect, camera)
        : undefined,
      anchorMarker: guide.kind === 'balloon' ? guide.anchorMarker ?? 'none' : undefined,
      label,
      title: guide.kind === 'balloon' ? guide.title : guide.label,
      valueText: guide.kind === 'balloon' ? guide.valueText : '',
      fields: guide.kind === 'balloon' ? guide.fields : undefined,
      key: guide.kind === 'control-point' ? guide.key : undefined,
      state: guide.kind === 'control-point' ? guide.state : undefined,
    };
  }

  /**
   * 解析气泡标签屏幕位置。
   *
   * @param guide 原始气泡引导。
   * @param viewport 视口适配器。
   * @param containerRect 视口容器边界。
   * @param camera 当前相机。
   * @param screenAnchor 气泡锚点屏幕坐标。
   * @returns 气泡标签屏幕坐标。
   */
  private resolveBalloonLabelPoint(
    guide: Extract<BlendPreviewGuideOverlayState['guides'][number], { kind: 'balloon' }>,
    viewport: BlendPreviewGuideViewportAdapter,
    containerRect: DOMRect,
    camera: THREE.Camera,
    screenAnchor: { x: number; y: number },
  ): { x: number; y: number } {
    if (!guide.labelDirectionAnchor) {
      return {
        x: screenAnchor.x + guide.offsetPx.x,
        y: screenAnchor.y + guide.offsetPx.y,
      };
    }
    const directionWorldPoint = viewport.resolveObjectWorldPoint(guide.sourceObjectId, guide.labelDirectionAnchor);
    const directionScreenPoint = directionWorldPoint
      ? this.projectWorldPoint(directionWorldPoint, containerRect, camera)
      : null;
    if (!directionScreenPoint) {
      return {
        x: screenAnchor.x + guide.offsetPx.x,
        y: screenAnchor.y + guide.offsetPx.y,
      };
    }
    const directionX = directionScreenPoint.x - screenAnchor.x;
    const directionY = directionScreenPoint.y - screenAnchor.y;
    const directionLength = Math.hypot(directionX, directionY);
    if (directionLength <= 1) {
      return {
        x: screenAnchor.x + guide.offsetPx.x,
        y: screenAnchor.y + guide.offsetPx.y,
      };
    }
    const labelDistance = Math.max(64, Math.hypot(guide.offsetPx.x, guide.offsetPx.y));
    return {
      x: screenAnchor.x + (directionX / directionLength) * labelDistance,
      y: screenAnchor.y + (directionY / directionLength) * labelDistance,
    };
  }

  /**
   * 投影气泡多引线锚点。
   *
   * @param guide 原始气泡引导。
   * @param viewport 视口适配器。
   * @param containerRect 视口容器边界。
   * @param camera 当前相机。
   * @returns 已投影的引线锚点集合。
   */
  private projectBalloonLeaderAnchors(
    guide: Extract<BlendPreviewGuideOverlayState['guides'][number], { kind: 'balloon' }>,
    viewport: BlendPreviewGuideViewportAdapter,
    containerRect: DOMRect,
    camera: THREE.Camera,
  ): Array<{ x: number; y: number }> {
    const leaderAnchors = guide.leaderAnchors && guide.leaderAnchors.length > 0
      ? guide.leaderAnchors
      : [{ anchor: guide.anchor, sourceRef: guide.sourceRef }];
    return leaderAnchors.flatMap((leaderAnchor) => {
      const worldPoint = viewport.resolveObjectWorldPoint(guide.sourceObjectId, leaderAnchor.anchor);
      if (!worldPoint) {
        return [];
      }
      const projectedPoint = this.projectWorldPoint(worldPoint, containerRect, camera);
      return projectedPoint ? [projectedPoint] : [];
    });
  }

  /**
   * 解析当前引导的世界坐标锚点。
   *
   * @param guide 原始引导。
   * @param viewport 视口适配器。
   * @returns 世界坐标锚点。
   */
  private resolveGuideWorldPoint(
    guide: BlendPreviewGuideOverlayState['guides'][number],
    viewport: BlendPreviewGuideViewportAdapter,
  ): [number, number, number] | null {
    const resolvedAnchor = viewport.resolveObjectWorldPoint(guide.sourceObjectId, guide.anchor);
    if (resolvedAnchor) {
      return resolvedAnchor;
    }

    if (guide.kind === 'control-point') {
      return null;
    }

    const matchedItem = this.latestState?.items.find((item) => item.persistentRef === guide.sourceRef) ?? null;
    if (matchedItem && typeof matchedItem.edgeIndex === 'number' && typeof viewport.getTopologyEdgeSegment === 'function') {
      const segment = viewport.getTopologyEdgeSegment(matchedItem.objectId, matchedItem.edgeIndex);
      if (segment) {
        return [
          (segment.start[0] + segment.end[0]) / 2,
          (segment.start[1] + segment.end[1]) / 2,
          (segment.start[2] + segment.end[2]) / 2,
        ];
      }
    }
    return null;
  }

  /**
   * 将世界坐标投影到屏幕空间。
   *
   * @param worldPoint 世界坐标点。
   * @param container 视口容器。
   * @param camera 当前相机。
   * @returns 屏幕坐标；不可见时返回 `null`。
   */
  private projectWorldPoint(
    worldPoint: [number, number, number],
    containerRect: DOMRect,
    camera: THREE.Camera,
  ): { x: number; y: number } | null {
    const projectedPoint = this.projectedPoint.set(worldPoint[0], worldPoint[1], worldPoint[2]).project(camera);
    if (projectedPoint.z < -1 || projectedPoint.z > 1) {
      return null;
    }

    return {
      x: (projectedPoint.x * 0.5 + 0.5) * containerRect.width,
      y: (-projectedPoint.y * 0.5 + 0.5) * containerRect.height,
    };
  }
}
